// COVER ELEMENTS (owner 2026-10-20): « make me able to add a sticker or icon
// over the cover however I want » + « add images from the already uploaded
// images to the back cover » + « I need to be able to drag and drop ».
//
// A cover element is a PageElement (the SAME model the content pages use — an
// icon/sticker with imageUrl + previewUrl, an admin SVG icon, or a photo with a
// photoId) plus the FACE it sits on. Its x / y / width / height are % of that
// face's printed frame, exactly like a page element is % of the page:
//
//   front, back  →  the 200 × 300 mm image frame (coverPanelGeometry)
//   spine        →  the printed spine column (board + both hinges) × the same
//                   300 mm frame height
//
// Stored in `album_sessions.cover_customizations.coverElements` (JSONB — no
// migration). Array order = layer order (last = on top), per face.
//
// PRINT PARITY. The editor draws each face as a box of the face's exact mm
// aspect and lays the elements in it with left/top/width/height % (the same
// PageElementOverlay the pages use). The PDF translates to the face's frame and
// calls the SAME drawPageElements the interior uses, clipped to the frame +
// its 3 mm bleed. Same %, same aspect → same place. Guard: coverElements.test.ts.
//
// THE COVER IS SACRED. No automatic process (AI layout, regen, notes executor,
// a cover switch) may drop or move these. Every writer of cover_customizations
// that rebuilds the object from scratch must carry them with
// `carryCoverElements`.
//
// Pure (no React, no DOM).

import type { PageElement } from './pdfGenerator';
import type { CoverPanelGeometry } from './coverPanelGeometry';
import { COVER_IMAGE_FRAME_W_MM, COVER_IMAGE_FRAME_H_MM, MM_TO_PT_ } from './coverPanelGeometry';
import { DEFAULT_PRINT_SETTINGS, type PrintSettings } from './assemblyTemplate';

export type CoverFace = 'front' | 'back' | 'spine';
export const COVER_FACES: readonly CoverFace[] = ['back', 'spine', 'front'];
export const COVER_FACE_LABEL: Record<CoverFace, string> = {
  front: 'Couverture avant',
  back: 'Quatrième de couverture',
  spine: 'Tranche',
};

export type CoverElement = PageElement & { face: CoverFace };

/** The most a cover can hold — a guard against a runaway loop, not a design limit. */
export const MAX_COVER_ELEMENTS = 40;

// ── Geometry ────────────────────────────────────────────────────────────────

/** The printed spine column (mm) — board + both hinges, as computeTemplateLayout. */
export function spineColumnMm(s: Pick<PrintSettings, 'spineWmm' | 'jointGapMm'> = DEFAULT_PRINT_SETTINGS): number {
  return s.spineWmm + 2 * s.jointGapMm;
}

/** A face's printed frame in mm (width, height). */
export function faceSizeMm(face: CoverFace, s: Pick<PrintSettings, 'spineWmm' | 'jointGapMm'> = DEFAULT_PRINT_SETTINGS): { w: number; h: number } {
  if (face === 'spine') return { w: spineColumnMm(s), h: COVER_IMAGE_FRAME_H_MM };
  return { w: COVER_IMAGE_FRAME_W_MM, h: COVER_IMAGE_FRAME_H_MM };
}

/** The face's width / height — the CSS aspect-ratio the editor's face box uses. */
export function faceAspect(face: CoverFace, s?: Pick<PrintSettings, 'spineWmm' | 'jointGapMm'>): number {
  const { w, h } = faceSizeMm(face, s);
  return w / h;
}

export interface RectPt { x: number; y: number; w: number; h: number }

/** Each face's frame on the cover sheet, in PDF points (origin bottom-left). */
export function coverFaceRectsPt(G: CoverPanelGeometry): Record<CoverFace, RectPt> {
  return {
    back:  { x: G.backX,  y: G.panelY, w: G.panelW, h: G.panelH },
    spine: { x: G.spineX, y: G.panelY, w: G.spineW, h: G.panelH },
    front: { x: G.frontX, y: G.panelY, w: G.panelW, h: G.panelH },
  };
}

/** Where the print CLIPS a face's elements: the frame plus the 3 mm bleed
 *  (front/back, all four sides; the spine keeps its column width and gets the
 *  bleed head and tail only — sideways it would run onto a board). */
export function coverFaceClipPt(face: CoverFace, G: CoverPanelGeometry): RectPt {
  const r = coverFaceRectsPt(G)[face];
  const b = G.bleed;
  if (face === 'spine') return { x: r.x, y: r.y - b, w: r.w, h: r.h + 2 * b };
  return { x: r.x - b, y: r.y - b, w: r.w + 2 * b, h: r.h + 2 * b };
}

/** The element's un-rotated box on the sheet, in PDF points — the box
 *  drawPageElements draws in once translated to the face (bottom-left origin). */
export function coverElementRectPt(el: Pick<CoverElement, 'face' | 'x' | 'y' | 'width' | 'height'>, G: CoverPanelGeometry): RectPt {
  const f = coverFaceRectsPt(G)[el.face];
  const w = (el.width / 100) * f.w;
  const h = (el.height / 100) * f.h;
  const x = f.x + (el.x / 100) * f.w;
  const yTop = (el.y / 100) * f.h;
  return { x, y: f.y + f.h - yTop - h, w, h };
}

/** The same box in mm from the SHEET's top-left corner (how a printer measures). */
export function coverElementRectMm(el: Pick<CoverElement, 'face' | 'x' | 'y' | 'width' | 'height'>, G: CoverPanelGeometry, sheetHmm: number): { left: number; top: number; w: number; h: number } {
  const r = coverElementRectPt(el, G);
  return {
    left: r.x / MM_TO_PT_,
    top: sheetHmm - (r.y + r.h) / MM_TO_PT_,
    w: r.w / MM_TO_PT_,
    h: r.h / MM_TO_PT_,
  };
}

/** The four corners of the element AFTER its rotation (CSS clockwise, about
 *  the centre), in mm on the face (top-left origin, y down) — the editor's
 *  `rotate()` and the print's matrix both turn about the same centre. */
export function rotatedCornersMm(el: Pick<CoverElement, 'face' | 'x' | 'y' | 'width' | 'height' | 'rotation'>, s?: Pick<PrintSettings, 'spineWmm' | 'jointGapMm'>): Array<{ x: number; y: number }> {
  const f = faceSizeMm(el.face, s);
  const w = (el.width / 100) * f.w, h = (el.height / 100) * f.h;
  const cx = (el.x / 100) * f.w + w / 2, cy = (el.y / 100) * f.h + h / 2;
  const th = ((el.rotation ?? 0) * Math.PI) / 180;
  const c = Math.cos(th), sn = Math.sin(th);
  return [[-w / 2, -h / 2], [w / 2, -h / 2], [w / 2, h / 2], [-w / 2, h / 2]].map(([dx, dy]) => ({
    x: cx + dx * c - dy * sn,
    y: cy + dx * sn + dy * c,
  }));
}

/**
 * Keep the element on its face: its ROTATED outline is shifted back inside the
 * frame (a box bigger than the face is centred on it). The editor clips the
 * face at the frame and the print at the frame + bleed — an element that stays
 * inside the frame looks the same on both, which is the parity rule.
 */
export function clampToFace<T extends Pick<CoverElement, 'face' | 'x' | 'y' | 'width' | 'height' | 'rotation'>>(el: T, s?: Pick<PrintSettings, 'spineWmm' | 'jointGapMm'>): T {
  const f = faceSizeMm(el.face, s);
  const c = rotatedCornersMm(el, s);
  const minX = Math.min(...c.map((p) => p.x)), maxX = Math.max(...c.map((p) => p.x));
  const minY = Math.min(...c.map((p) => p.y)), maxY = Math.max(...c.map((p) => p.y));
  const shift = (lo: number, hi: number, size: number) =>
    hi - lo > size ? size / 2 - (lo + hi) / 2 : lo < 0 ? -lo : hi > size ? size - hi : 0;
  const dx = shift(minX, maxX, f.w), dy = shift(minY, maxY, f.h);
  if (Math.abs(dx) < 1e-9 && Math.abs(dy) < 1e-9) return el;
  return { ...el, x: el.x + (dx / f.w) * 100, y: el.y + (dy / f.h) * 100 };
}

// ── Placing ─────────────────────────────────────────────────────────────────

/** First-placement size, as a share of the face: a sticker is a quarter of the
 *  front's width (the same as on a page), at most a quarter of its height; on
 *  the narrow spine it takes 80 % of the column, at most a fifth of the height. */
function defaultBoxMm(face: CoverFace, aspect: number, kind: 'sticker' | 'photo', s?: Pick<PrintSettings, 'spineWmm' | 'jointGapMm'>): { w: number; h: number } {
  const f = faceSizeMm(face, s);
  const shareW = face === 'spine' ? 0.8 : kind === 'photo' ? 0.45 : 0.25;
  const shareH = face === 'spine' ? 0.2 : kind === 'photo' ? 0.45 : 0.25;
  const a = aspect > 0 && Number.isFinite(aspect) ? aspect : 1;
  let w = f.w * shareW;
  let h = w / a;
  if (h > f.h * shareH) { h = f.h * shareH; w = h * a; }
  return { w, h };
}

/** Centre a box of `widthPct × heightPct` on the point (fx, fy) — fractions of
 *  the face (0..1) — and keep it fully on the face. No point = the face centre. */
export function boxAtPoint(widthPct: number, heightPct: number, at?: { fx: number; fy: number } | null): { x: number; y: number } {
  const cx = (at ? at.fx : 0.5) * 100;
  const cy = (at ? at.fy : 0.5) * 100;
  const clamp = (v: number, size: number) => Math.max(0, Math.min(100 - size, v));
  return { x: clamp(cx - widthPct / 2, widthPct), y: clamp(cy - heightPct / 2, heightPct) };
}

export const newCoverElementId = () => `cel-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

/** An image element (sticker / catalogue icon / admin SVG) on a face, at its
 *  true aspect in millimetres, centred on the drop point. */
export function coverImageElement(
  src: { imageUrl?: string; previewUrl?: string; svg?: string; aspect: number; shadow?: boolean },
  face: CoverFace,
  at?: { fx: number; fy: number } | null,
  s?: Pick<PrintSettings, 'spineWmm' | 'jointGapMm'>,
  id: string = newCoverElementId(),
): CoverElement {
  const f = faceSizeMm(face, s);
  const box = defaultBoxMm(face, src.aspect, 'sticker', s);
  const width = (box.w / f.w) * 100;
  const height = (box.h / f.h) * 100;
  const { x, y } = boxAtPoint(width, height, at);
  return {
    id, face, type: 'icon', x, y, width, height,
    ...(src.imageUrl ? { imageUrl: src.imageUrl } : {}),
    ...(src.previewUrl ? { previewUrl: src.previewUrl } : {}),
    ...(src.svg ? { svg: src.svg } : {}),
    ...(src.shadow ? { shadow: true } : {}),
  };
}

/** A photo the client uploaded (photos.id), at its own shape, on a face. */
export function coverPhotoElement(
  photoId: string,
  photoAspect: number,
  face: CoverFace,
  at?: { fx: number; fy: number } | null,
  s?: Pick<PrintSettings, 'spineWmm' | 'jointGapMm'>,
  id: string = newCoverElementId(),
): CoverElement {
  const f = faceSizeMm(face, s);
  const box = defaultBoxMm(face, photoAspect, 'photo', s);
  const width = (box.w / f.w) * 100;
  const height = (box.h / f.h) * 100;
  const { x, y } = boxAtPoint(width, height, at);
  return { id, face, type: 'photo', photoId, x, y, width, height };
}

// ── Reading / layering ──────────────────────────────────────────────────────

/** The largest box side / offset kept, in % of the face (see readCoverElements). */
export const MAX_BOX_PCT = 1500;

const num = (v: unknown, lo: number, hi: number): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? Math.max(lo, Math.min(hi, v)) : null;

/** The stored list, cleaned: icons that name a picture (imageUrl or svg),
 *  photos that name a photo, and TEXT that carries its words (owner 2026-10-30:
 *  « I need to be able to use the text … type whatever I want ») — on a known
 *  face, with finite numbers. Unknown fields are KEPT (a newer editor's field
 *  must survive an older reader).
 *
 *  ⚠️ Text was silently DROPPED here until 2026-10-30, so a long poem on the
 *  back of #WA-363209 had to be baked into a picture. It is a first-class
 *  element now: the print draws it with drawPageElements' text path (vector
 *  outlines first, the canvas raster as fallback) — the interior's own. */
export function readCoverElements(raw: unknown): CoverElement[] {
  if (!Array.isArray(raw)) return [];
  const out: CoverElement[] = [];
  const seen = new Set<string>();
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const e = r as Record<string, unknown>;
    const face = e.face;
    if (face !== 'front' && face !== 'back' && face !== 'spine') continue;
    if (typeof e.id !== 'string' || !e.id || seen.has(e.id)) continue;
    const isIcon = e.type === 'icon' && ((typeof e.imageUrl === 'string' && /^https?:\/\//.test(e.imageUrl)) || typeof e.svg === 'string');
    const isPhoto = e.type === 'photo' && typeof e.photoId === 'string' && !!e.photoId;
    const isText = e.type === 'text' && typeof e.text === 'string';
    if (!isIcon && !isPhoto && !isText) continue;
    // A spine text runs ALONG the spine: a box 200 mm long on a 26 mm column is
    // ~770 % of the face width (turned 90°) — hence the wide bounds.
    const width = num(e.width, 0.5, MAX_BOX_PCT);
    const height = num(e.height, 0.5, MAX_BOX_PCT);
    const x = num(e.x, -MAX_BOX_PCT, MAX_BOX_PCT);
    const y = num(e.y, -MAX_BOX_PCT, MAX_BOX_PCT);
    if (width == null || height == null || x == null || y == null) continue;
    const rotation = num(e.rotation, -360, 360);
    const opacity = num(e.opacity, 0, 1);
    seen.add(e.id);
    if (isText) {
      const fontSize = num(e.fontSize, 1, 4000);
      const lineHeight = num(e.lineHeight, 0.6, 4);
      const bandPad = num(e.bandPad, 0, 50);
      out.push({
        ...(e as unknown as CoverElement),
        face, x, y, width, height,
        ...(rotation != null ? { rotation } : { rotation: undefined }),
        ...(opacity != null ? { opacity } : { opacity: undefined }),
        fontSize: fontSize ?? 24,
        ...(lineHeight != null ? { lineHeight } : { lineHeight: undefined }),
        ...(bandPad != null ? { bandPad } : { bandPad: undefined }),
      });
      if (out.length >= MAX_COVER_ELEMENTS) break;
      continue;
    }
    out.push({
      ...(e as unknown as CoverElement),
      face, x, y, width, height,
      ...(rotation != null ? { rotation } : { rotation: undefined }),
      ...(opacity != null ? { opacity } : { opacity: undefined }),
    });
    if (out.length >= MAX_COVER_ELEMENTS) break;
  }
  return out;
}

/** A face's elements, bottom layer first. */
export function elementsOnFace(all: readonly CoverElement[], face: CoverFace): CoverElement[] {
  return all.filter((e) => e.face === face);
}

export type LayerMove = 'up' | 'down' | 'top' | 'bottom';

/** Move one element up/down a layer AMONG ITS FACE'S elements (other faces'
 *  elements keep their slots in the array). */
export function moveLayer(all: readonly CoverElement[], id: string, move: LayerMove): CoverElement[] {
  const el = all.find((e) => e.id === id);
  if (!el) return [...all];
  const slots = all.map((e, i) => (e.face === el.face ? i : -1)).filter((i) => i >= 0);
  const face = slots.map((i) => all[i]);
  const k = face.findIndex((e) => e.id === id);
  const next = [...face];
  next.splice(k, 1);
  const to = move === 'top' ? next.length
    : move === 'bottom' ? 0
    : move === 'up' ? Math.min(next.length, k + 1)
    : Math.max(0, k - 1);
  next.splice(to, 0, el);
  const out = [...all];
  slots.forEach((slot, j) => { out[slot] = next[j]; });
  return out;
}

/** The element moved to another face, its centre kept at the same relative
 *  place, its millimetre size kept (re-expressed in the new face's %). */
export function moveToFace(el: CoverElement, face: CoverFace, s?: Pick<PrintSettings, 'spineWmm' | 'jointGapMm'>): CoverElement {
  if (el.face === face) return el;
  const from = faceSizeMm(el.face, s), to = faceSizeMm(face, s);
  let wMm = (el.width / 100) * from.w, hMm = (el.height / 100) * from.h;
  const fit = Math.min(1, to.w / wMm, to.h / hMm);
  wMm *= fit; hMm *= fit;
  const width = (wMm / to.w) * 100, height = (hMm / to.h) * 100;
  const fx = (el.x + el.width / 2) / 100, fy = (el.y + el.height / 2) / 100;
  return { ...el, face, width, height, ...boxAtPoint(width, height, { fx, fy }) };
}

/**
 * The cover-is-sacred carry: a writer that REBUILDS cover_customizations (a
 * cover switch, the cover library) passes the previous object here so the
 * admin's stickers and back photos survive. Returns `next` with the previous
 * `coverElements` when `next` does not name its own.
 */
export function carryCoverElements<T extends Record<string, unknown>>(prev: unknown, next: T): T {
  if (Object.prototype.hasOwnProperty.call(next, 'coverElements')) return next;
  const kept = readCoverElements((prev as { coverElements?: unknown } | null | undefined)?.coverElements);
  return kept.length ? { ...next, coverElements: kept } : next;
}

// ── Text (owner 2026-10-30) ─────────────────────────────────────────────────
// « I need to be able to use the text, make it bigger, make it smaller … write
// a phrase and make it big … control the width and the height of it ».
//
// A cover text is the interior's text PageElement on a face: `fontSize` is px
// on a 400-px-wide FACE (the editor draws it at `fontSize/4 cqi` of the face
// layer, the print at fontSize × faceW / 400 — pdfTextLayout.textRasterPx with
// the face width as the page). The words wrap at the BOX width; the box height
// is the frame the team draws (a text taller than it still shows and prints,
// centred on the box — flagged in the editor). `lineHeight` (default 1.3) and
// `bandPad` (the band's margin round the box, % of the face width) are the
// two fields the cover adds.

export const COVER_TEXT_DEFAULT = 'Votre texte';
export const COVER_TEXT_DEFAULT_FONT = "'Fraunces', 'Cormorant Garamond', Georgia, serif";

/** The printed size of a text, in points, for the size readout (« 24 pt »). */
export function coverTextPt(fontSize: number, face: CoverFace, s?: Pick<PrintSettings, 'spineWmm' | 'jointGapMm'>): number {
  return (fontSize * faceSizeMm(face, s).w * MM_TO_PT_) / 400;
}

/** The `fontSize` that prints at `pt` points on `face` (coverTextPt's inverse). */
export function coverFontSizeForPt(pt: number, face: CoverFace, s?: Pick<PrintSettings, 'spineWmm' | 'jointGapMm'>): number {
  return (pt * 400) / (faceSizeMm(face, s).w * MM_TO_PT_);
}

/**
 * A new text on a face. Front / back: a 140 × 30 mm box at 28 pt, centred on
 * the point. Spine: turned 90° to read along it, a box as long as 2/3 of the
 * spine and as thick as the spine BOARD, at 70 % of that thickness.
 */
export function coverTextElement(
  face: CoverFace,
  at?: { fx: number; fy: number } | null,
  s?: Pick<PrintSettings, 'spineWmm' | 'jointGapMm'>,
  opts: { text?: string; fontFamily?: string; fontColor?: string } = {},
  id: string = newCoverElementId(),
): CoverElement {
  const f = faceSizeMm(face, s);
  const base = {
    id, face, type: 'text' as const,
    text: opts.text ?? COVER_TEXT_DEFAULT,
    fontFamily: opts.fontFamily ?? COVER_TEXT_DEFAULT_FONT,
    fontColor: opts.fontColor ?? '#1F2937',
    fontWeight: '600',
    fontStyle: 'normal' as const,
    textAlign: 'center' as const,
    lineHeight: 1.3,
  };
  if (face === 'spine') {
    const spineW = s?.spineWmm ?? DEFAULT_PRINT_SETTINGS.spineWmm;
    const lenMm = f.h * (2 / 3);
    const thickMm = Math.max(4, spineW);
    const width = (lenMm / f.w) * 100;
    const height = (thickMm / f.h) * 100;
    const cx = (at ? at.fx : 0.5) * 100, cy = (at ? at.fy : 0.5) * 100;
    // The em that prints at 70 % of the board thickness (mm → fontSize).
    const fontSize = ((thickMm * 0.7) / f.w) * 400;
    return clampToFace({ ...base, x: cx - width / 2, y: cy - height / 2, width, height, rotation: 90, fontSize }, s);
  }
  const width = (140 / f.w) * 100;
  const height = (30 / f.h) * 100;
  const { x, y } = boxAtPoint(width, height, at);
  return { ...base, x, y, width, height, fontSize: coverFontSizeForPt(28, face, s) };
}

// ── The printable safe zone (owner 2026-10-30) ──────────────────────────────
// « The client wants it bigger, but also well-framed — not cut out when I flip
// it on the hardcover. »
//
// Computed from the SAME print settings the cover PDF is built from
// (app_settings.print_template_settings → coverPanelGeometry), never from
// screen constants:
//
//   front / back  the BOARD (boardW × boardH) minus `safeZoneMm` on all four
//                 edges — the « cutting / wrapping safe zone » of the settings
//                 (the vinyl folds round the board there; a hand-laid sheet
//                 shifts a few mm). Expressed in the face's 200 × 300 mm frame,
//                 which sits centred on the board.
//   spine         the spine BOARD only (`spineWmm`, centred in the printed
//                 column): the hinge grooves (`jointGapMm` each side) fold as
//                 the book opens. Head and tail lose `safeZoneMm` as the boards.
//
// Defaults (board 200 × 303, safe 5, spine 10, hinge 8): front/back safe =
// x 5 → 195 mm, y 3.5 → 296.5 mm of the frame; spine safe = x 8 → 18 mm of
// the 26 mm column.

export type SafeZoneSettings = Pick<PrintSettings, 'boardWmm' | 'boardHmm' | 'spineWmm' | 'jointGapMm' | 'safeZoneMm'>;

export interface RectMm { x: number; y: number; w: number; h: number }

/** The face's safe rectangle in mm on the face (top-left origin, y down). */
export function coverSafeZoneMm(face: CoverFace, s: SafeZoneSettings = DEFAULT_PRINT_SETTINGS): RectMm {
  const f = faceSizeMm(face, s);
  const safe = Math.max(0, s.safeZoneMm);
  // The frame (COVER_IMAGE_FRAME) is centred on the board (coverPanelGeometry).
  const oy = (s.boardHmm - COVER_IMAGE_FRAME_H_MM) / 2;
  const y0 = Math.max(0, safe - oy);
  const y1 = Math.min(f.h, s.boardHmm - safe - oy);
  if (face === 'spine') {
    const x0 = Math.max(0, s.jointGapMm);
    const x1 = Math.min(f.w, s.jointGapMm + s.spineWmm);
    return { x: x0, y: y0, w: Math.max(0, x1 - x0), h: Math.max(0, y1 - y0) };
  }
  const ox = (s.boardWmm - COVER_IMAGE_FRAME_W_MM) / 2;
  const x0 = Math.max(0, safe - ox);
  const x1 = Math.min(f.w, s.boardWmm - safe - ox);
  return { x: x0, y: y0, w: Math.max(0, x1 - x0), h: Math.max(0, y1 - y0) };
}

/** The same rectangle in % of the face (what the editor draws). */
export function coverSafeZonePct(face: CoverFace, s: SafeZoneSettings = DEFAULT_PRINT_SETTINGS): { x: number; y: number; w: number; h: number } {
  const f = faceSizeMm(face, s);
  const r = coverSafeZoneMm(face, s);
  return { x: (r.x / f.w) * 100, y: (r.y / f.h) * 100, w: (r.w / f.w) * 100, h: (r.h / f.h) * 100 };
}

/**
 * What of an element really prints: its box — or, for a text, the box grown to
 * the words' height when they are taller (centred on it, as drawn) and the
 * band's margin round it. `textHeightPct` is the measured height of the words
 * in % of the face height (the editor measures it; absent = fits the box).
 */
export function printedExtent<T extends Pick<CoverElement, 'face' | 'x' | 'y' | 'width' | 'height' | 'rotation' | 'type' | 'bandFill' | 'bandPad'>>(
  el: T, s?: Pick<PrintSettings, 'spineWmm' | 'jointGapMm'>, textHeightPct?: number | null,
): Pick<CoverElement, 'face' | 'x' | 'y' | 'width' | 'height' | 'rotation'> {
  let { x, y, width, height } = el;
  if (el.type === 'text') {
    if (textHeightPct != null && textHeightPct > height) {
      y = y + height / 2 - textHeightPct / 2;
      height = textHeightPct;
    }
    const pad = el.bandFill && el.bandPad ? el.bandPad : 0;
    if (pad > 0) {
      const f = faceSizeMm(el.face, s);
      const padYPct = ((pad / 100) * f.w / f.h) * 100; // bandPad is % of the face WIDTH, all sides
      x -= pad; width += 2 * pad; y -= padYPct; height += 2 * padYPct;
    }
  }
  return { face: el.face, x, y, width, height, rotation: el.rotation };
}

const SAFE_EPS_MM = 0.05;

/** True when any corner of what prints (rotation included) leaves the safe zone. */
export function crossesSafeZone(
  el: Pick<CoverElement, 'face' | 'x' | 'y' | 'width' | 'height' | 'rotation' | 'type' | 'bandFill' | 'bandPad'>,
  s: SafeZoneSettings = DEFAULT_PRINT_SETTINGS,
  textHeightPct?: number | null,
): boolean {
  const z = coverSafeZoneMm(el.face, s);
  return rotatedCornersMm(printedExtent(el, s, textHeightPct), s).some((p) =>
    p.x < z.x - SAFE_EPS_MM || p.x > z.x + z.w + SAFE_EPS_MM || p.y < z.y - SAFE_EPS_MM || p.y > z.y + z.h + SAFE_EPS_MM);
}

/**
 * « Mettre dans la zone sûre »: shrink the element (about its centre) when what
 * prints is bigger than the safe zone — a text shrinks its letters, band margin
 * and box together so its lines stay the same — then slide it inside.
 */
export function fitIntoSafeZone<T extends CoverElement>(el: T, s: SafeZoneSettings = DEFAULT_PRINT_SETTINGS, textHeightPct?: number | null): T {
  const z = coverSafeZoneMm(el.face, s);
  const f = faceSizeMm(el.face, s);
  const bbox = (e: T, th?: number | null) => {
    const c = rotatedCornersMm(printedExtent(e, s, th), s);
    const xs = c.map((p) => p.x), ys = c.map((p) => p.y);
    return { x0: Math.min(...xs), x1: Math.max(...xs), y0: Math.min(...ys), y1: Math.max(...ys) };
  };
  let out: T = el;
  let th = textHeightPct;
  const b0 = bbox(out, th);
  const k0 = Math.min(1, z.w / Math.max(1e-6, b0.x1 - b0.x0), z.h / Math.max(1e-6, b0.y1 - b0.y0));
  const k = k0 < 1 ? k0 * (1 - 1e-6) : 1;
  if (k < 1) {
    const cx = out.x + out.width / 2, cy = out.y + out.height / 2;
    const width = out.width * k, height = out.height * k;
    out = {
      ...out, width, height, x: cx - width / 2, y: cy - height / 2,
      ...(out.type === 'text' && out.fontSize ? { fontSize: out.fontSize * k } : {}),
      ...(out.type === 'text' && out.bandPad ? { bandPad: out.bandPad * k } : {}),
    };
    if (th != null) th *= k;
  }
  const b = bbox(out, th);
  const dx = b.x0 < z.x ? z.x - b.x0 : b.x1 > z.x + z.w ? z.x + z.w - b.x1 : 0;
  const dy = b.y0 < z.y ? z.y - b.y0 : b.y1 > z.y + z.h ? z.y + z.h - b.y1 : 0;
  if (dx || dy) out = { ...out, x: out.x + (dx / f.w) * 100, y: out.y + (dy / f.h) * 100 };
  return out;
}
